// Internal-only AI stack:
//   - Claude Fable 5  → all text / analysis / variants / copy / blog
//   - GPT Image 2     → image generation (OpenAI's GPT-5.5-era image system)
//   - Gemini removed  → no longer selectable anywhere; legacy selections alias to Fable 5
export const OPENAI_LATEST_MODEL = "gpt-5.5";
export const OPENAI_FAST_MODEL = "gpt-5.5-mini";
export const OPENAI_IMAGE_MODEL = "gpt-image-2";
export const SEEDANCE_MODEL = "bytedance/seedance-2.0-fast/text-to-video";
export const SEEDANCE_IMAGE_TO_VIDEO_MODEL = "bytedance/seedance-2.0/image-to-video";

export const CLAUDE_DEFAULT_MODEL = "claude-fable-5";
// Opus 4.8 — selectable alongside Fable 5 for creative analysis. Like Fable 5 it
// rejects temperature/top_p/top_k (adaptive thinking only); the AI proxy strips
// those before forwarding (see sanitizeAnthropicPayload in api/ai/generate.js).
export const CLAUDE_OPUS_MODEL = "claude-opus-4-8";
export const SORA_DEFAULT_MODEL = "sora-2-pro";

// Retained only so residual internal references in AIService resolve. Gemini is
// never offered in the UI and every stored Gemini selection aliases to Fable 5,
// so these code paths are unreachable in normal use. Do not add them to any
// *_MODEL_OPTIONS list.
export const GEMINI_FAST_MODEL = "gemini-2.5-flash";
export const GEMINI_QUALITY_MODEL = "gemini-2.5-pro";
export const GEMINI_VISION_MODEL = CLAUDE_DEFAULT_MODEL;
export const GEMINI_IMAGE_MODEL = OPENAI_IMAGE_MODEL;

export const DEFAULT_ANALYSIS_MODEL = CLAUDE_DEFAULT_MODEL;
export const DEFAULT_POST_ANALYSIS_MODEL = CLAUDE_DEFAULT_MODEL;
export const DEFAULT_VARIANT_MODEL = CLAUDE_DEFAULT_MODEL;
export const DEFAULT_BLOG_MODEL = CLAUDE_DEFAULT_MODEL;
export const DEFAULT_POST_COPY_MODEL = CLAUDE_DEFAULT_MODEL;
export const DEFAULT_IMAGE_MODEL = OPENAI_IMAGE_MODEL;
export const DEFAULT_VIDEO_MODEL = SEEDANCE_MODEL;

const MODEL_LABELS = {
  [OPENAI_LATEST_MODEL]: "GPT-5.5 (OpenAI)",
  [OPENAI_FAST_MODEL]: "GPT-5.5 Mini (OpenAI)",
  [OPENAI_IMAGE_MODEL]: "GPT Image 2 (OpenAI)",
  [SEEDANCE_MODEL]: "Seedance 2.0 Fast (ByteDance / AtlasCloud)",
  [CLAUDE_DEFAULT_MODEL]: "Claude Fable 5 (Anthropic)",
  [CLAUDE_OPUS_MODEL]: "Claude Opus 4.8 (Anthropic)",
  [SORA_DEFAULT_MODEL]: "Sora 2 Pro",
};

export const TEXT_MODEL_OPTIONS = [
  { value: CLAUDE_DEFAULT_MODEL, label: MODEL_LABELS[CLAUDE_DEFAULT_MODEL] },
  { value: OPENAI_LATEST_MODEL, label: MODEL_LABELS[OPENAI_LATEST_MODEL] },
  { value: OPENAI_FAST_MODEL, label: MODEL_LABELS[OPENAI_FAST_MODEL] },
];

export const ANALYSIS_MODEL_OPTIONS = [
  { value: CLAUDE_DEFAULT_MODEL, label: MODEL_LABELS[CLAUDE_DEFAULT_MODEL] },
  { value: CLAUDE_OPUS_MODEL, label: MODEL_LABELS[CLAUDE_OPUS_MODEL] },
  { value: OPENAI_LATEST_MODEL, label: MODEL_LABELS[OPENAI_LATEST_MODEL] },
];

export const IMAGE_MODEL_OPTIONS = [
  { value: OPENAI_IMAGE_MODEL, label: MODEL_LABELS[OPENAI_IMAGE_MODEL] },
];

export const VIDEO_MODEL_OPTIONS = [
  { value: SEEDANCE_MODEL, label: MODEL_LABELS[SEEDANCE_MODEL] },
  { value: SORA_DEFAULT_MODEL, label: MODEL_LABELS[SORA_DEFAULT_MODEL] },
];

export const NEURO_MODEL_OPTIONS = [
  { value: CLAUDE_DEFAULT_MODEL, label: MODEL_LABELS[CLAUDE_DEFAULT_MODEL] },
  { value: OPENAI_LATEST_MODEL, label: MODEL_LABELS[OPENAI_LATEST_MODEL] },
];

// Every legacy/removed model id migrates to the current defaults so stored
// user_settings selections keep working after the upgrade.
const LEGACY_MODEL_ALIASES = {
  "gpt-4o": OPENAI_LATEST_MODEL,
  "gpt-4o-mini": OPENAI_FAST_MODEL,
  "gpt-5": OPENAI_LATEST_MODEL,
  "gpt-5.1": OPENAI_LATEST_MODEL,
  "gpt-5.2": OPENAI_LATEST_MODEL,
  "gpt-5.4": OPENAI_LATEST_MODEL,
  "gpt-5.4-mini": OPENAI_FAST_MODEL,
  "gpt-5.4-nano": OPENAI_FAST_MODEL,
  "gpt-image-2-2026-04-21": OPENAI_IMAGE_MODEL,
  "claude-opus-4-1-20250805": CLAUDE_DEFAULT_MODEL,
  "claude-opus-4-6": CLAUDE_DEFAULT_MODEL,
  "claude-opus-4-6-20250415": CLAUDE_DEFAULT_MODEL,
  // Gemini is retired — anything Gemini becomes Fable 5.
  "gemini-2.5-flash": CLAUDE_DEFAULT_MODEL,
  "gemini-2.5-pro": CLAUDE_DEFAULT_MODEL,
  "gemini-3-pro-preview": CLAUDE_DEFAULT_MODEL,
  "gemini-3-pro-image-preview": OPENAI_IMAGE_MODEL,
};

export const normalizeModelSelection = (
  value,
  fallback = DEFAULT_ANALYSIS_MODEL,
) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return fallback;
  if (LEGACY_MODEL_ALIASES[trimmed]) return LEGACY_MODEL_ALIASES[trimmed];
  // Any other gemini-* id also retires to Fable 5.
  if (trimmed.toLowerCase().startsWith("gemini")) return CLAUDE_DEFAULT_MODEL;
  return trimmed;
};

export const getModelLabel = (value) => {
  const normalized = normalizeModelSelection(value, "");
  return MODEL_LABELS[normalized] || normalized || "Default";
};
